import { useEffect } from 'react';

import { triggerTapHaptic } from '@/core/lib/dnd-haptics';

/**
 * Global, delegated listener that fires a light tap haptic whenever the user
 * completes a tap/click on a button-like element.
 *
 * Handled elements:
 *  - Any native `<button>`.
 *  - Elements with `role="button"` (Radix triggers, custom buttons via `asChild`).
 *  - Elements carrying `data-slot="button"` (our shared UI button — covers the
 *    slot variant that renders e.g. an `<a>` via `asChild`).
 *
 * Opt out per element by adding `data-no-haptic="true"` on the element or any
 * ancestor (useful for toolbars where we don't want buzzing on every click).
 *
 * On web this is a no-op because `@capacitor/haptics` only fires on native;
 * the delegated listener still runs but the underlying call short-circuits.
 *
 * Implementation: we track the button element that received `pointerdown` and
 * only fire haptics when `pointerup` lands on the same element. This prevents
 * spurious buzzing when a drag ends over a button or when a pointer moves away
 * from the element between down and up.
 */
export function useButtonHaptics(): void {
  useEffect(() => {
    let pointerDownTarget: Element | null = null;

    const resolveButton = (event: PointerEvent): HTMLElement | null => {
      // Only react to primary touch/mouse input, not right-click, etc.
      if (event.button !== 0) return null;

      const target = event.target;
      if (!(target instanceof Element)) return null;

      const button = target.closest<HTMLElement>(
        'button, [role="button"], [data-slot="button"]',
      );
      if (!button) return null;

      // Respect disabled state.
      if (button.hasAttribute('disabled')) return null;
      if (button.getAttribute('aria-disabled') === 'true') return null;

      // Opt-out marker on the button itself or any ancestor wrapper.
      if (button.closest('[data-no-haptic="true"]')) return null;

      return button;
    };

    const onPointerDown = (event: PointerEvent) => {
      pointerDownTarget = resolveButton(event);
    };

    const onPointerCancel = () => {
      pointerDownTarget = null;
    };

    const onPointerUp = (event: PointerEvent) => {
      const button = resolveButton(event);
      if (button && button === pointerDownTarget) {
        void triggerTapHaptic();
      }
      pointerDownTarget = null;
    };

    document.addEventListener('pointerdown', onPointerDown, { capture: true });
    document.addEventListener('pointercancel', onPointerCancel, {
      capture: true,
    });
    document.addEventListener('pointerup', onPointerUp, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, {
        capture: true,
      });
      document.removeEventListener('pointercancel', onPointerCancel, {
        capture: true,
      });
      document.removeEventListener('pointerup', onPointerUp, { capture: true });
    };
  }, []);
}
